import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import Select from "@/components/Select";
import GenericAlertModal from "@/components/AlertModal";

import Stack from "@mui/material/Stack";

import useDebounce from "@/hooks/useDebounce";
import { usePageStore } from "@/hooks/usePageStore";
import { useActions, useArchiveAction } from "@/hooks/useActions";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useAppTheme } from "@/utils/theme";
import { toTitleCase } from "@/utils/toTitleCase";

import { IAction } from "@/domain/actions.interface";
import { ErrorResponse } from "@/domain/error.interface";
import {
  ACTION_STATUS_OPTIONS,
  ACTION_TYPE_OPTIONS,
  ActionStatusEnum,
} from "@/domain/actions.enum";

import ActionModal from "@/ui/modals/actions/ActionModal";

type Filters = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  status: string;
  type: string;
};

const defaultFilters: Filters = {
  sortField: "order",
  sortOrder: 1,
  pageNumber: 0,
  pageSize: 10,
  search: "",
  status: "",
  type: "",
};

const ActionsPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { showSnackBar } = useFeedBack();

  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const debouncedSearch = useDebounce({ value: filters.search, delay: 500 });

  const [openActionModal, setOpenActionModal] = useState(false);
  const [selectedAction, setSelectedAction] = useState<IAction | null>(null);

  const [genericAlert, setGenericAlert] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    onConfirm: () => undefined,
  });

  const archiveAction = useArchiveAction();

  const { actions, isLoadingActions, isFetchingActions, refetchActions } =
    useActions({
      search: debouncedSearch,
      status: filters.status || undefined,
      type: filters.type || undefined,
      pageNumber: filters.pageNumber,
      pageSize: filters.pageSize,
      sortField: filters.sortField,
      sortOrder: filters.sortOrder,
    });

  const { actions: actionsCatalog } = useActions({
    pageNumber: 0,
    pageSize: 500,
    sortField: "name",
    sortOrder: 1,
  });

  const actionNameById = useMemo(() => {
    const map = new Map<string, string>();
    (actionsCatalog?.list || []).forEach((a) => {
      if (a._id) map.set(a._id, a.name || a._id);
    });
    return map;
  }, [actionsCatalog]);

  useEffect(() => {
    setPageTitle("Actions");
  }, [setPageTitle]);

  const handleArchive = async (actionId: string) => {
    try {
      await archiveAction.mutateAsync(actionId, {
        onSuccess: () => {
          refetchActions();
          showSnackBar("The registry has been deleted.", "success");
          setGenericAlert((prev) => ({ ...prev, open: false }));
        },
      });
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const rows = useMemo(() => {
    if (!actions || actions.list.length === 0) return [];

    return actions.list.map((item, index) => {
      const status = (item.status || ActionStatusEnum.ACTIVE) as string;
      const isActive = status === ActionStatusEnum.ACTIVE;

      const actionMenu = [
        {
          value: "Edit",
          onClick: () => {
            setSelectedAction(item);
            setOpenActionModal(true);
          },
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () => {
            setSelectedAction(item);
            setGenericAlert({
              open: true,
              title: "Delete",
              message: "Are you sure you want to delete this registry?",
              onConfirm: () => handleArchive(item._id),
            });
          },
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const subActionIds: string[] = Array.isArray(item.subActions)
        ? (item.subActions as unknown[]).map((sa) =>
            typeof sa === "string" ? sa : ((sa as IAction)?._id ?? "")
          )
        : [];
      const subActionNames = subActionIds
        .map((id) => actionNameById.get(id) || id)
        .filter(Boolean);

      return createDataRow(index, {
        name: <Typography variant="bodyMedium">{item.name}</Typography>,
        code: <Typography variant="bodyMedium">{item.code}</Typography>,
        type: (
          <Typography variant="bodyMedium">
            {item.type ? toTitleCase(item.type.replace(/_/g, " ")) : "-"}
          </Typography>
        ),
        order: <Typography variant="bodyMedium">{item.order}</Typography>,
        subActions:
          subActionNames.length > 0 ? (
            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ rowGap: 0.5 }}
            >
              {subActionNames.map((name, i) => (
                <Chip
                  key={`${item._id}-sub-${i}`}
                  size="small"
                  label={<div className="text-xs">{name}</div>}
                />
              ))}
            </Stack>
          ) : (
            <Typography variant="bodyMedium">-</Typography>
          ),
        status: (
          <Chip
            sx={{
              bgcolor: isActive
                ? theme.palette.brand.light
                : theme.palette.warning.light,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: isActive
                ? theme.palette.brand.main
                : theme.palette.warning.dark,
            }}
            label={<div className="text-xs">{toTitleCase(status)}</div>}
          />
        ),
        action: <ActionMenu id={item._id} options={actionMenu} />,
      });
    });
  }, [actions, theme, actionNameById]);

  const headCells: HeadCell[] = [
    {
      id: "name",
      isSortable: true,
      disablePadding: true,
      label: "Name",
      align: "left",
      width: "220px",
    },
    {
      id: "code",
      isSortable: true,
      disablePadding: false,
      label: "Code",
      align: "left",
      width: "260px",
    },
    {
      id: "type",
      isSortable: true,
      disablePadding: false,
      label: "Type",
      align: "left",
      width: "140px",
    },
    {
      id: "order",
      isSortable: true,
      disablePadding: false,
      label: "Order",
      align: "left",
      width: "80px",
    },
    {
      id: "subActions",
      isSortable: false,
      disablePadding: false,
      label: "Sub actions",
      align: "left",
      width: "260px",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
      width: "120px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: "Actions",
      align: "right",
    },
  ];

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Actions
                  </Typography>

                  <Box display="flex" gap="0.5rem">
                    <Button
                      size="medium"
                      onClick={() => {
                        setSelectedAction(null);
                        setOpenActionModal(true);
                      }}
                      variant="contained"
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        textAlign: "center",
                        height: "40px",
                        backgroundColor: theme.palette.primary.dark,
                        color: theme.palette.textMain.light,
                      }}
                      startIcon={<AddIcon />}
                    >
                      ADD
                    </Button>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: "0.5rem",
                    width: "100%",
                    flexDirection: {
                      xs: "column",
                      sm: "column",
                      md: "row",
                    },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: theme.palette.surface.light2,
                      px: 2,
                      width: { xs: "100%", sm: "100%" },
                    }}
                    className="flex rounded-lg py-3 max-w-full items-center justify-between h-14"
                  >
                    <InputBase
                      className="flex-1"
                      placeholder="Search action"
                      inputProps={{ "aria-label": "search" }}
                      onChange={(e) => {
                        setFilters((prev) => ({
                          ...prev,
                          search: e.target.value,
                          pageNumber: 0,
                        }));
                      }}
                      value={filters.search}
                    />
                    <SearchIcon className="text-icon-primary" />
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: "0.5rem",
                      width: { xs: "100%", sm: "100%" },
                      flexDirection: {
                        xs: "column",
                        sm: "row",
                        md: "row",
                      },
                    }}
                  >
                    <Select
                      id="actions-status"
                      label="Status"
                      handleChange={(value) => {
                        setFilters((prev) => ({
                          ...prev,
                          status: value as string,
                          pageNumber: 0,
                        }));
                      }}
                      data={ACTION_STATUS_OPTIONS}
                      value={filters.status}
                      showClearOption
                      maxWidth={240}
                    />
                    <Select
                      id="actions-type"
                      label="Type"
                      handleChange={(value) => {
                        setFilters((prev) => ({
                          ...prev,
                          type: value as string,
                          pageNumber: 0,
                        }));
                      }}
                      data={ACTION_TYPE_OPTIONS}
                      value={filters.type}
                      showClearOption
                      maxWidth={240}
                    />
                  </Box>
                </Box>

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={actions?.totalCount}
                  isLoading={isLoadingActions || isFetchingActions}
                  onChangePage={(_event, newPage) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: newPage,
                    }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      sortField: orderBy as string,
                      sortOrder: order === "asc" ? 1 : -1,
                    }));
                  }}
                  messageEmptyData="No actions to show"
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {openActionModal ? (
        <ActionModal
          open={openActionModal}
          action={selectedAction}
          onClose={() => {
            setOpenActionModal(false);
            setSelectedAction(null);
            refetchActions();
          }}
        />
      ) : null}

      {genericAlert.open ? (
        <GenericAlertModal
          onCancel={() => setGenericAlert((prev) => ({ ...prev, open: false }))}
          onClick={() => {
            genericAlert.onConfirm();
          }}
          title={genericAlert.title}
          description={genericAlert.message}
          submitText="Confirm"
          cancelText="Cancel"
          theme={theme}
        />
      ) : null}
    </>
  );
};

export default ActionsPage;
